/* Field — the one text input used everywhere in the product.
 *
 * A faithful port of the console's `Field`, down to the transform values, so
 * the two surfaces are pixel-identical while both stacks exist. Everything it
 * does, this does:
 *
 *   - a floating label that rises when the field has focus or content
 *   - a password reveal (eye / eye-off)
 *   - leading slots: an icon, or a text prefix like +91 with its own divider
 *   - trailing slots: clear, a status icon, arbitrary content
 *   - error and success states, each with a matching icon and message colour
 *   - help text that the error replaces rather than sits beside
 *   - textarea and select variants, and a compact size for dense forms
 *
 * The styles live in field.css rather than Tailwind classes: the floating label
 * needs :focus-within and sibling state that reads far better as real CSS, and
 * keeping it in one file means the port stays diffable against cn-ui.css.
 */
import {
  forwardRef,
  useId,
  useState,
  type CSSProperties,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react'
import { AlertCircle, Check, ChevronDown, Eye, EyeOff, X } from 'lucide-react'
import { cn } from '@/lib/utils'

type Common = {
  label: string
  error?: string | null
  success?: string | null
  help?: string
  /** Text shown before the input, e.g. "+91". Gets its own divider. */
  prefix?: string
  /** An interactive leading slot in the prefix's place — used to put a
   *  tappable `🇮🇳 +91 ▾` where the static text would go. Takes precedence
   *  over `prefix`, and shares its divider and alignment so a picker and a
   *  plain prefix are indistinguishable until clicked. */
  prefixSlot?: ReactNode
  /** Width of the leading slot, when the content is wider than a dial code
   *  alone. The floating label starts after it; too small and the label sits
   *  on top of the slot. */
  prefixWidth?: number
  leadIcon?: ReactNode
  trailIcon?: ReactNode
  /** Adds the show/hide password button. Only meaningful on a password field. */
  reveal?: boolean
  /** Adds a clear button while the field has content. */
  clearable?: boolean
  onClear?: () => void
  size?: 'md' | 'sm'
  className?: string
}

export type FieldProps = Common &
  Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'prefix'>

export const Field = forwardRef<HTMLInputElement, FieldProps>(function Field(
  {
    label,
    error,
    success,
    help,
    prefix,
    prefixSlot,
    prefixWidth,
    leadIcon,
    trailIcon,
    reveal,
    clearable,
    onClear,
    size = 'md',
    className,
    type = 'text',
    value,
    required,
    disabled,
    readOnly,
    id: idProp,
    ...props
  },
  ref,
) {
  const reactId = useId()
  const id = idProp ?? reactId
  const [revealed, setRevealed] = useState(false)

  const filled = value != null && value !== ''
  const state = error ? 'error' : success ? 'success' : null

  // A date input renders its own placeholder text, so its label must float
  // immediately or the two overlap.
  const alwaysFloat = type === 'date' || type === 'time'

  const inputType = reveal && revealed ? 'text' : type

  const lead = prefixSlot ?? (prefix ? prefix : null)

  return (
    <div
      className={cn(
        'cn-field',
        size === 'sm' && 'cn-field--sm',
        (leadIcon || lead) && 'cn-field--lead',
        // A text prefix is wider than an icon, so the label needs a bigger
        // offset — see --cn-lead-w in field.css.
        lead && 'cn-field--prefix',
        (filled || alwaysFloat) && 'cn-field--float',
        state && `cn-field--${state}`,
        disabled && 'cn-field--disabled',
        readOnly && 'cn-field--readonly',
        className,
      )}
      // Overrides --cn-lead-w for a slot wider than a dial code, so the
      // resting label starts clear of it instead of on top of it.
      style={prefixWidth ? ({ '--cn-lead-w': `${prefixWidth}px` } as CSSProperties) : undefined}
    >
      <div className="cn-field__box">
        {lead ? <span className="cn-field__prefix">{lead}</span> : null}
        {leadIcon ? <span className="cn-field__lead">{leadIcon}</span> : null}

        <input
          ref={ref}
          id={id}
          type={inputType}
          value={value}
          required={required}
          disabled={disabled}
          readOnly={readOnly}
          // A space, not empty: :placeholder-shown is what tells CSS the field
          // is empty, and it never matches without a placeholder to show.
          placeholder={props.placeholder ?? ' '}
          aria-invalid={Boolean(error)}
          aria-describedby={error || success || help ? `${id}-msg` : undefined}
          className="cn-field__input"
          {...props}
        />

        <label className="cn-field__label" htmlFor={id}>
          {label}
          {required ? ' *' : ''}
        </label>

        <div className="cn-field__trail">
          {reveal ? (
            <button
              type="button"
              className="cn-field__btn"
              // The label states what the click will DO, and it changes with
              // the state — a fixed "Show password" lies once it is showing.
              aria-label={revealed ? 'Hide password' : 'Show password'}
              aria-pressed={revealed}
              onClick={() => setRevealed((v) => !v)}
              // Keeps focus in the input: tabbing to the eye and back is not
              // how anyone fills in a form.
              tabIndex={-1}
            >
              {revealed ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          ) : null}

          {clearable && filled ? (
            <button
              type="button"
              className="cn-field__btn"
              aria-label="Clear"
              onClick={onClear}
              tabIndex={-1}
            >
              <X size={18} />
            </button>
          ) : null}

          {trailIcon ? <span className="cn-field__lead">{trailIcon}</span> : null}

          {state ? (
            <span
              className="cn-field__lead"
              style={{
                color: `var(--cn-${state === 'error' ? 'danger' : 'success'})`,
              }}
              aria-hidden
            >
              {state === 'error' ? <AlertCircle size={18} /> : <Check size={18} />}
            </span>
          ) : null}
        </div>
      </div>

      <FieldMessage id={`${id}-msg`} error={error} success={success} help={help} />
    </div>
  )
})

/** The line under a field. The error replaces the help text rather than adding
 *  to it — two messages at once is one more than anybody reads. */
function FieldMessage({
  id,
  error,
  success,
  help,
}: {
  id: string
  error?: string | null
  success?: string | null
  help?: string
}) {
  const text = error || success || help
  if (!text) return null
  return (
    <div
      id={id}
      className="cn-field__msg"
      // Errors are announced; help text is not, because it was already there
      // when the field was read.
      role={error ? 'alert' : undefined}
    >
      {error ? <AlertCircle size={14} aria-hidden /> : null}
      {text}
    </div>
  )
}

/* ----------------------------------------------------------------- textarea */

export type TextFieldProps = Common &
  Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'size'>

export const TextArea = forwardRef<HTMLTextAreaElement, TextFieldProps>(
  function TextArea(
    { label, error, success, help, size = 'md', className, value, required,
      disabled, readOnly, id: idProp, ...props },
    ref,
  ) {
    const reactId = useId()
    const id = idProp ?? reactId
    const filled = value != null && value !== ''
    const state = error ? 'error' : success ? 'success' : null

    return (
      <div
        className={cn(
          'cn-field cn-field--area',
          size === 'sm' && 'cn-field--sm',
          filled && 'cn-field--float',
          state && `cn-field--${state}`,
          disabled && 'cn-field--disabled',
          readOnly && 'cn-field--readonly',
          className,
        )}
      >
        <div className="cn-field__box">
          <textarea
            ref={ref}
            id={id}
            value={value}
            required={required}
            disabled={disabled}
            readOnly={readOnly}
            placeholder={props.placeholder ?? ' '}
            aria-invalid={Boolean(error)}
            aria-describedby={error || success || help ? `${id}-msg` : undefined}
            className="cn-field__input"
            rows={props.rows ?? 4}
            {...props}
          />
          <label className="cn-field__label" htmlFor={id}>
            {label}
            {required ? ' *' : ''}
          </label>
        </div>
        <FieldMessage id={`${id}-msg`} error={error} success={success} help={help} />
      </div>
    )
  },
)

/* ------------------------------------------------------------------- select */

export type SelectFieldProps = Common &
  Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> & {
    options: { value: string; label: string }[]
  }

export const SelectField = forwardRef<HTMLSelectElement, SelectFieldProps>(
  function SelectField(
    { label, error, success, help, options, size = 'md', className, value,
      required, disabled, id: idProp, ...props },
    ref,
  ) {
    const reactId = useId()
    const id = idProp ?? reactId
    const state = error ? 'error' : success ? 'success' : null

    return (
      <div
        className={cn(
          // A select always floats: it shows its current value from the start,
          // so a centred label would sit on top of it.
          'cn-field cn-field--select cn-field--float',
          size === 'sm' && 'cn-field--sm',
          state && `cn-field--${state}`,
          disabled && 'cn-field--disabled',
          className,
        )}
      >
        <div className="cn-field__box">
          <select
            ref={ref}
            id={id}
            value={value}
            required={required}
            disabled={disabled}
            aria-invalid={Boolean(error)}
            aria-describedby={error || success || help ? `${id}-msg` : undefined}
            className="cn-field__input"
            {...props}
          >
            {options.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <label className="cn-field__label" htmlFor={id}>
            {label}
            {required ? ' *' : ''}
          </label>
          <span className="cn-field__chev" aria-hidden>
            <ChevronDown size={18} />
          </span>
        </div>
        <FieldMessage id={`${id}-msg`} error={error} success={success} help={help} />
      </div>
    )
  },
)
